import { UserName } from "../components/UserName.tsx";
import { ResultEffects } from "../components/ResultEffects.tsx";
import { leaveResultThroughAd } from "../lib/adGate.ts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MAX_ATTEMPTS,
  STANDARD_TEAM_LOBBY_SETTINGS,
  mergeKeyboardStates,
  type AckResult,
  type GuessResponse,
  type LetterState,
  type TeamHistoryEntry,
  type TeamId,
  type TeamLobbySettings,
  type TeamLobbyState,
  type TeamLobbySummary,
  type TeamRacePlayerView,
  type TeamRaceState,
} from "@mk/shared";
import { Board } from "../components/Board.tsx";
import { DuelClock } from "../components/DuelClock.tsx";
import { GhostBoard } from "../components/GhostBoard.tsx";
import { Keyboard } from "../components/Keyboard.tsx";
import { ResultBoard } from "../components/ResultBoard.tsx";
import { ScreenHeader } from "../components/ScreenHeader.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { Toast } from "../components/Toast.tsx";
import { useBoardInput } from "../features/useBoardInput.ts";
import {
  advanceTeamCountdownAudio,
  EMPTY_TEAM_COUNTDOWN_AUDIO_STATE,
  teamMatchClock,
} from "../features/team/teamCountdown.ts";
import { useTeamStore } from "../features/team/teamStore.ts";
import { advanceDuelAudio, EMPTY_DUEL_AUDIO_STATE } from "../features/duel/duelAudio.ts";
import { createDraftRelay, toDraftCellsWire } from "../features/duel/draftRelay.ts";
import { errorMessage, messages } from "../i18n/index.ts";
import { api } from "../lib/api.ts";
import { getSocket, serverNow } from "../lib/socket.ts";
import { useSession } from "../lib/session.ts";
import type { RegisterBackHandler } from "../lib/navigation.ts";
import { playSound, stopTimerSounds } from "../lib/sound.ts";
import { APP_EDITION, gameEdition } from "../lib/edition.ts";
import { publicOrigin } from "../lib/native.ts";
import { teamInviteUrl } from "../features/team/inviteLink.ts";
import {
  EMPTY_FRIENDS_DATA,
  type FriendsData,
} from "../features/friends/types.ts";

interface TeamScreenProps {
  onBack: () => void;
  registerBackHandler: RegisterBackHandler;
  /** Davet bağlantısından gelen lobi kodu; ekran açılınca katılınır. */
  inviteCode?: string | null;
  onInviteHandled?: () => void;
}

/** Davet bağlantısını paylaşır; paylaşım yoksa panoya kopyalar. Sonuç notu döner. */
async function shareTeamInvite(code: string): Promise<string | null> {
  const url = teamInviteUrl(publicOrigin(), code, APP_EDITION);
  if (typeof navigator.share === "function") {
    try {
      await navigator.share({ title: messages.team.inviteShareTitle, text: messages.team.inviteShareText(code), url });
      return null;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return null;
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return messages.team.inviteCopied;
  } catch {
    return url;
  }
}

function teamName(team: TeamId): string {
  return team === "A" ? messages.team.teamA : messages.team.teamB;
}

function LobbyCard({ summary, onJoin, onWatch }: {
  summary: TeamLobbySummary;
  onJoin: () => void;
  onWatch: () => void;
}) {
  const live = summary.status === "live";
  return (
    <article className="team-lobby-card" data-live={live ? "true" : undefined}>
      <div>
        <span className="eyebrow">{live ? messages.spectate.live : summary.code}</span>
        <h3>{<UserName user={summary.host} />}</h3>
        <p>{messages.team.minutes(summary.settings.durationMinutes)} · {summary.playerCount}/4</p>
      </div>
      <div className="team-lobby-card__score">
        <b className="text-mint">A {summary.teamCounts.A}</b>
        <span>:</span>
        <b className="text-flame">{summary.teamCounts.B} B</b>
      </div>
      <button type="button" className={live ? "chip-btn" : "btn-primary"} onClick={live ? onWatch : onJoin}>
        {live ? messages.team.watch : messages.team.join}
      </button>
    </article>
  );
}

function SettingsFields({ value, onChange }: {
  value: TeamLobbySettings;
  onChange: (settings: TeamLobbySettings) => void;
}) {
  return (
    <div className="team-settings">
      <fieldset>
        <legend>{messages.team.duration}</legend>
        <div className="segmented">
          {([1, 2, 3] as const).map((durationMinutes) => (
            <button key={durationMinutes} type="button" aria-pressed={value.durationMinutes === durationMinutes}
              onClick={() => onChange({ ...value, durationMinutes })}>
              {messages.team.minutes(durationMinutes)}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend>{messages.team.spectators}</legend>
        <div className="segmented">
          {[true, false].map((spectatorsAllowed) => (
            <button key={String(spectatorsAllowed)} type="button" aria-pressed={value.spectatorsAllowed === spectatorsAllowed}
              onClick={() => onChange({ ...value, spectatorsAllowed })}>
              {spectatorsAllowed ? messages.team.on : messages.team.off}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend>{messages.team.visibility}</legend>
        <div className="segmented">
          {(["public", "code-only"] as const).map((visibility) => (
            <button key={visibility} type="button" aria-pressed={value.visibility === visibility}
              onClick={() => onChange({ ...value, visibility })}>
              {visibility === "public" ? messages.team.public : messages.team.codeOnly}
            </button>
          ))}
        </div>
      </fieldset>
    </div>
  );
}

function TeamHowTo({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Sheet open={open} title={messages.team.helpTitle} onClose={onClose}>
      <p className="text-sm leading-relaxed text-muted">{messages.team.helpIntro}</p>
      <ol className="team-howto-list">
        {messages.team.helpRules.map((rule, index) => (
          <li key={rule.title}>
            <b>{index + 1}</b>
            <div><strong>{rule.title}</strong><p>{rule.text}</p></div>
          </li>
        ))}
      </ol>
    </Sheet>
  );
}

function TeamHelpButton({ onClick }: { onClick: () => void }) {
  return <button type="button" className="btn-ghost" onClick={onClick} aria-label={messages.team.helpOpen}>?</button>;
}

function LobbyView({ lobby, onBack, onHelp }: { lobby: TeamLobbyState; onBack: () => void; onHelp: () => void }) {
  const me = useSession((state) => state.user);
  const [settings, setSettings] = useState(lobby.settings);
  const [notice, setNotice] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [friendsData, setFriendsData] = useState<FriendsData>(EMPTY_FRIENDS_DATA);
  const [inviteBusyId, setInviteBusyId] = useState<string | null>(null);
  const socket = getSocket();
  const host = lobby.hostId === me?.id;

  useEffect(() => setSettings(lobby.settings), [lobby.settings]);

  const loadInviteFriends = useCallback(async () => {
    try {
      setFriendsData(await api<FriendsData>("/api/friends"));
    } catch {
      setNotice(messages.friendsScreen.listFailed);
    }
  }, []);

  useEffect(() => {
    if (!inviteOpen) return;
    const onPresence = ({ userId, online }: { userId: string; online: boolean }) => {
      setFriendsData((current) => ({
        ...current,
        friends: current.friends.map((friend) =>
          friend.user.id === userId ? { ...friend, online } : friend,
        ),
      }));
    };
    const refresh = () => void loadInviteFriends();
    socket.on("friend:presence", onPresence);
    socket.on("friends:changed", refresh);
    socket.on("team:invite:cancelled", refresh);
    return () => {
      socket.off("friend:presence", onPresence);
      socket.off("friends:changed", refresh);
      socket.off("team:invite:cancelled", refresh);
    };
  }, [inviteOpen, loadInviteFriends, socket]);

  const inviteFriends = useMemo(() => [...friendsData.friends].sort(
    (a, b) => Number(b.online) - Number(a.online) ||
      gameEdition.compareWords(a.user.username, b.user.username),
  ), [friendsData.friends]);
  const invitedIds = useMemo(() => new Set(
    friendsData.sentTeamInvites
      .filter((invite) => invite.lobbyId === lobby.lobbyId)
      .map((invite) => invite.to.id),
  ), [friendsData.sentTeamInvites, lobby.lobbyId]);
  const lobbyMemberIds = useMemo(
    () => new Set(lobby.members.map((member) => member.user.id)),
    [lobby.members],
  );

  const act = <T,>(event: string, payload: unknown, onSuccess?: (data: T) => void) => {
    socket.emit(event, payload, (response: AckResult<T>) => {
      if (!response.ok) setNotice(errorMessage(response));
      else onSuccess?.(response.data);
    });
  };

  const openInvites = () => {
    setInviteOpen(true);
    void loadInviteFriends();
  };

  const invite = (friendId: string) => {
    if (inviteBusyId) return;
    setInviteBusyId(friendId);
    socket.emit("team:invite:send", { lobbyId: lobby.lobbyId, friendId }, (response: AckResult<{ inviteId: string; expiresAt: number }>) => {
      setInviteBusyId(null);
      if (!response.ok) {
        setNotice(errorMessage(response));
        return;
      }
      setNotice(messages.team.teamInviteSent);
      void loadInviteFriends();
    });
  };

  return (
    <div className="app team-app">
      <ScreenHeader title={messages.team.lobby} subtitle={lobby.code} onBack={onBack} actions={<TeamHelpButton onClick={onHelp} />} />
      <main className="overflow-y-auto px-4 pb-6">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
          {notice && <p className="toast-static">{notice}</p>}
          <button type="button" className="team-code" onClick={() => {
            void navigator.clipboard?.writeText(lobby.code);
            setNotice(messages.team.copied);
          }}>
            <span>{messages.team.code}</span><b>{lobby.code}</b><small>{messages.team.copyCode}</small>
          </button>
          <button type="button" className="btn-secondary w-full" onClick={() => {
            void shareTeamInvite(lobby.code).then((note) => { if (note) setNotice(note); });
          }}>
            {messages.team.shareInvite}
          </button>

          <div className="team-columns">
            {(["A", "B"] as const).map((team) => (
              <section key={team} className="team-column" data-team={team}>
                <h2>{teamName(team)} <span>{lobby.teamCounts[team]}/2</span></h2>
                {lobby.members.filter((member) => member.team === team).map((member) => (
                  <div className="team-member" key={member.user.id}>
                    <span className="presence-dot" data-online={member.connected ? "true" : undefined} />
                    <b>{<UserName user={member.user} />}</b>
                    {member.user.id === lobby.hostId && <small>{messages.team.host}</small>}
                    {host && member.user.id !== me?.id && lobby.status !== "live" && (
                      <button type="button" className="chip-remove" onClick={() => act("team:lobby:kick", { lobbyId: lobby.lobbyId, userId: member.user.id })}>
                        {messages.team.kick}
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="team-member team-member--empty"
                  disabled={lobby.members.find((member) => member.user.id === me?.id)?.team === team}
                  aria-label={team === "A" ? messages.team.moveA : messages.team.moveB}
                  onClick={() => act("team:lobby:set-team", { lobbyId: lobby.lobbyId, team })}>
                  <span aria-hidden="true">＋</span>
                </button>
              </section>
            ))}
          </div>

          {host && lobby.status !== "live" && (
            <>
              <button type="button" className="btn-primary team-invite-open" disabled={lobby.playerCount >= 4} onClick={openInvites}>
                {messages.team.inviteFriends}
              </button>
              <section className="team-settings-panel" data-open={settingsOpen ? "true" : undefined}>
                <button
                  type="button"
                  className="team-settings-toggle"
                  aria-expanded={settingsOpen}
                  aria-controls="team-lobby-settings"
                  onClick={() => setSettingsOpen((open) => !open)}
                >
                  <span><b>{messages.team.settings}</b><small>{messages.team.settingsSummary(
                    settings.durationMinutes,
                    settings.spectatorsAllowed ? messages.team.on : messages.team.off,
                    settings.visibility === "public" ? messages.team.public : messages.team.codeOnly,
                  )}</small></span>
                  <i aria-hidden="true">⌄</i>
                </button>
                {settingsOpen && (
                  <div id="team-lobby-settings">
                    <SettingsFields value={settings} onChange={(next) => {
                      setSettings(next);
                      act("team:lobby:update", { lobbyId: lobby.lobbyId, settings: next });
                    }} />
                  </div>
                )}
              </section>
            </>
          )}

          <p className="text-center text-xs text-muted">{messages.team.startHint}</p>
          {host && (
            <button type="button" className="btn-primary" disabled={!lobby.canStart}
              onClick={() => act("team:lobby:start", { lobbyId: lobby.lobbyId })}>
              {messages.team.start}
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={onBack}>{messages.team.leave}</button>
        </div>
      </main>
      <Sheet open={inviteOpen} title={messages.team.inviteFriendsTitle} onClose={() => setInviteOpen(false)}>
        <p className="mb-4 text-sm text-muted">{messages.team.inviteFriendsHint}</p>
        <ul className="team-invite-list">
          {inviteFriends.length === 0 && <li className="text-sm text-muted">{messages.team.noInviteFriends}</li>}
          {inviteFriends.map((friend) => {
            const inLobby = lobbyMemberIds.has(friend.user.id);
            const invited = invitedIds.has(friend.user.id);
            return (
              <li key={friend.user.id} className="team-invite-friend">
                <span className="presence-dot" data-online={friend.online ? "true" : undefined} />
                <b>{<UserName user={friend.user} />}</b>
                <button
                  type="button"
                  className="chip-btn chip-btn--primary"
                  disabled={lobby.playerCount >= 4 || inLobby || invited || inviteBusyId !== null}
                  onClick={() => invite(friend.user.id)}
                >
                  {inLobby ? messages.team.inLobby : invited ? messages.team.invited : messages.team.invite}
                </button>
              </li>
            );
          })}
        </ul>
      </Sheet>
    </div>
  );
}

/*
 * Şerit koridora göre gruplanıyor. Eskiden "AX / AY / BX / BY" kodlarıyla
 * takım sırasına diziliydi; oyuncunun kodu çözmesi gerekiyordu ve asıl önemli
 * bilgi olan "kim kiminle aynı kelimede yarışıyor" görünmüyordu. Artık her
 * kutu bir koridor: solda A takımı, sağda B takımı, aralarında "vs".
 */
function TeamScore({ state, remainingMs }: { state: TeamRaceState; remainingMs: number }) {
  return (
    <div className="team-score-stack">
      <div className="team-scorebar">
        <div data-team="A"><span>{messages.team.teamA}</span><b>{state.score.A.words}</b></div>
        {/* 2v2'de bitiş anı sabit; süre kısalma rozeti burada yanlış alarm. */}
        <DuelClock remainingMs={remainingMs} canShorten={false} />
        <div data-team="B"><b>{state.score.B.words}</b><span>{messages.team.teamB}</span></div>
      </div>
      <div className="team-lane-strip">
        {(["X", "Y"] as const).map((lane) => {
          const inLane = (team: TeamId) =>
            state.players.find((player) => player.lane === lane && player.team === team);
          const a = inLane("A");
          const b = inLane("B");
          const mine = a?.user.id === state.viewerUserId || b?.user.id === state.viewerUserId;
          return (
            <div key={lane} className="team-lane-strip__lane" data-mine={mine ? "true" : undefined}>
              <span className="team-lane-strip__label">
                {messages.team.lane(lane)}
                {mine && <i>{messages.team.yourLane}</i>}
              </span>
              <span className="team-lane-strip__pair">
                <b data-team="A" data-me={a?.user.id === state.viewerUserId ? "true" : undefined}>
                  {a?.user.username ?? "—"}
                </b>
                <em data-team="A">{a?.wordsSolved ?? 0}</em>
                <i aria-hidden="true">{messages.team.versus}</i>
                <em data-team="B">{b?.wordsSolved ?? 0}</em>
                <b data-team="B" data-me={b?.user.id === state.viewerUserId ? "true" : undefined}>
                  {b?.user.username ?? "—"}
                </b>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlayerMiniBoard({ player }: { player: TeamRacePlayerView }) {
  return player.guesses ? (
    <ResultBoard rows={player.guesses} draft={player.draft} padTo={MAX_ATTEMPTS} />
  ) : (
    <GhostBoard rows={player.patterns} label={player.user.username} solvedAt={null}
      attempts={player.patterns.length} hint={messages.ghost.colorsOnly} />
  );
}

function SmallPlayerBoard({ player, full = false }: { player: TeamRacePlayerView; full?: boolean }) {
  return (
    <div className="team-player-card" data-team={player.team} data-full={full ? "true" : undefined}>
      <header><b>{<UserName user={player.user} />}</b><span>{messages.team.lane(player.lane)} · {player.wordsSolved} {messages.team.words}</span></header>
      <PlayerMiniBoard player={player} />
    </div>
  );
}

/*
 * Canlı ekranda seçici kartın kendi başlık sütununda durur. Ayrı bir satır
 * olduğunda kendi tahtasından bir satır çalıyordu; başlık sütunu ise mini
 * tahtanın yanında zaten boştu.
 */
function SecondaryPanel({ player, mode, onMode }: {
  player: TeamRacePlayerView;
  mode: "team" | "rival";
  onMode: (mode: "team" | "rival") => void;
}) {
  return (
    <div className="team-player-card team-player-card--secondary" data-team={player.team}>
      <header>
        <b>{<UserName user={player.user} />}</b>
        <span>{messages.team.lane(player.lane)} · {player.wordsSolved} {messages.team.words}</span>
        <div className="team-secondary-tabs">
          <button type="button" aria-pressed={mode === "team"} onClick={() => onMode("team")}>{messages.team.teammate}</button>
          <button type="button" aria-pressed={mode === "rival"} onClick={() => onMode("rival")}>{messages.team.laneOpponent}</button>
        </div>
      </header>
      <PlayerMiniBoard player={player} />
    </div>
  );
}

interface LiveSeats {
  me: TeamRacePlayerView;
  teammate: TeamRacePlayerView;
  laneRival: TeamRacePlayerView;
}

/** Oyuncu düzeninin gerektirdiği üç kart; biri eksikse null. */
function liveSeats(state: TeamRaceState): LiveSeats | null {
  const me = state.players.find((player) => player.user.id === state.viewerUserId);
  if (!me) return null;
  const teammate = state.players.find((player) => player.team === me.team && player.user.id !== me.user.id);
  const laneRival = state.players.find((player) => player.team !== me.team && player.lane === me.lane);
  return teammate && laneRival ? { me, teammate, laneRival } : null;
}

function LivePlayerView({ state, seats, onBack }: {
  state: TeamRaceState;
  seats: LiveSeats;
  onBack: () => void;
}) {
  const { me, teammate, laneRival } = seats;
  const [secondary, setSecondary] = useState<"team" | "rival">("team");
  const [notice, setNotice] = useState<string | null>(null);
  const noticeTimer = useRef<number | null>(null);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  const [clock, setClock] = useState(() =>
    teamMatchClock(state.startsAt, state.endsAt, serverNow()),
  );
  const { countdownSecond, remainingMs, started } = clock;
  const socket = getSocket();
  const audioState = useRef(EMPTY_DUEL_AUDIO_STATE);
  const countdownAudioState = useRef(EMPTY_TEAM_COUNTDOWN_AUDIO_STATE);
  const solvedForWord = useRef<string | null>(null);
  const currentRows = me.guesses ?? [];
  const inTransition = me.transitionEndsAt !== null;

  const clearNotice = useCallback(() => {
    if (noticeTimer.current !== null) {
      window.clearTimeout(noticeTimer.current);
      noticeTimer.current = null;
    }
    setNotice(null);
  }, []);
  const showNotice = useCallback((message: string) => {
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
    setNotice(message);
    noticeTimer.current = window.setTimeout(() => {
      noticeTimer.current = null;
      setNotice(null);
    }, 1700);
  }, []);

  useEffect(() => () => {
    if (noticeTimer.current !== null) window.clearTimeout(noticeTimer.current);
  }, []);
  useEffect(() => {
    if (!notice) return;
    const dismiss = () => clearNotice();
    window.addEventListener("pointerdown", dismiss, true);
    window.addEventListener("keydown", dismiss, true);
    return () => {
      window.removeEventListener("pointerdown", dismiss, true);
      window.removeEventListener("keydown", dismiss, true);
    };
  }, [clearNotice, notice]);

  const submit = useCallback((word: string | null) => {
    if (!word || sendingRef.current) {
      if (!word) showNotice(messages.feedback.lengthRequired(5));
      return;
    }
    sendingRef.current = true;
    setSending(true);
    socket.timeout(8_000).emit(
      "team:guess:submit",
      {
        matchId: state.matchId,
        word,
        wordIndex: me.wordIndex,
        row: currentRows.length,
      },
      (timeout: Error | null, response: AckResult<GuessResponse>) => {
        sendingRef.current = false;
        setSending(false);
        if (timeout) {
          showNotice(messages.feedback.guessConnectionFailed);
          return;
        }
        // Kabul edilen tahmin önceki uyarıyı siler; yarış temposunda ekranda
        // kalan eski "kelime listede yok" satırı yanıltıyordu.
        if (response.ok) clearNotice();
        else showNotice(errorMessage(response));
      },
    );
  }, [clearNotice, currentRows.length, me.wordIndex, showNotice, socket, state.matchId]);
  const input = useBoardInput({ disabled: !started || sending || inTransition || remainingMs <= 0, onSubmit: submit });
  const clearDraft = input.clear;
  // Satır da kimliğe dahil: tahmin kabul edilince yazılan harfler bir sonraki
  // satıra taşınmasın diye tahta her gönderimden sonra boşalır.
  const identity = `${state.matchId}:${me.wordIndex}:${currentRows.length}`;
  const [draftIdentity, setDraftIdentity] = useState(identity);
  useEffect(() => {
    if (draftIdentity === identity) return;
    clearDraft();
    sendingRef.current = false;
    setSending(false);
    setDraftIdentity(identity);
  }, [clearDraft, draftIdentity, identity]);
  useEffect(() => {
    const tick = () => {
      setClock(teamMatchClock(state.startsAt, state.endsAt, serverNow()));
    };
    tick();
    const id = window.setInterval(() => {
      tick();
    }, 200);
    return () => window.clearInterval(id);
  }, [state.endsAt, state.startsAt]);
  useEffect(() => {
    const transition = advanceTeamCountdownAudio(countdownAudioState.current, {
      matchId: state.matchId,
      second: countdownSecond,
      visible: document.visibilityState === "visible",
    });
    countdownAudioState.current = transition.state;
    for (const effect of transition.effects) playSound(effect);
  }, [countdownSecond, state.matchId]);
  useEffect(() => {
    const transition = advanceDuelAudio(audioState.current, {
      matchId: state.matchId,
      live: started && remainingMs > 0,
      remainingMs,
      visible: document.visibilityState === "visible",
    });
    audioState.current = transition.state;
    for (const effect of transition.effects) playSound(effect);
    if (remainingMs <= 0) stopTimerSounds();
  }, [remainingMs, started, state.matchId]);
  useEffect(() => () => stopTimerSounds(), []);
  useEffect(() => {
    const solved = currentRows.at(-1)?.pattern.every((cell) => cell === "correct") ?? false;
    const key = `${state.matchId}:${me.wordIndex}`;
    if (solved && solvedForWord.current !== key) {
      solvedForWord.current = key;
      playSound("solved");
    }
  }, [currentRows, me.wordIndex, state.matchId]);
  /*
   * Taslak yayını düellodaki denetleyiciyi paylaşır: aynı harf dizisi ikinci
   * kez gönderilmez, satır değişince geçmiş sıfırlanır ve tahta kilitliyken
   * (geçiş, sayım, süre bitti) hiç yayın yapılmaz.
   */
  const draftRelay = useMemo(
    () => createDraftRelay((payload) => { getSocket().emit("team:draft:update", payload); }),
    [],
  );
  useEffect(() => () => draftRelay.stop(), [draftRelay]);
  useEffect(() => {
    draftRelay.update({
      enabled: started && !inTransition && remainingMs > 0 && draftIdentity === identity,
      matchId: state.matchId,
      row: currentRows.length,
      cells: toDraftCellsWire(input.cells),
    });
  }, [currentRows.length, draftIdentity, draftRelay, identity, inTransition, input.cells, remainingMs, started, state.matchId]);
  const keyboardStates = useMemo(
    () => currentRows.reduce<Record<string, LetterState>>(
      (states, guess) => mergeKeyboardStates(states, guess.word, guess.pattern),
      {},
    ),
    [currentRows],
  );

  return (
    <div className="app team-app team-app--live">
      <ScreenHeader title={messages.team.title} subtitle={messages.team.lane(me.lane)} onBack={onBack}
        actions={state.spectators > 0 ? <span className="eyebrow">{messages.team.spectatorCount(state.spectators)}</span> : undefined} />
      <TeamScore state={state} remainingMs={remainingMs} />
      <main className="team-live-main">
        <Toast message={notice} />
        <SecondaryPanel player={secondary === "team" ? teammate : laneRival}
          mode={secondary} onMode={setSecondary} />
        <div className="team-own-board" data-team={me.team}>
          <div className="team-own-board__meta"><b>{<UserName user={me.user} />}</b><span>{me.wordsSolved} {messages.team.words} · {me.completedAttempts} {messages.team.attempts}</span></div>
          {countdownSecond !== null && (
            <div className="team-countdown-overlay" role="status" aria-live="polite" aria-atomic="true">
              <span key={`${state.matchId}:${countdownSecond}`}>{countdownSecond}</span>
            </div>
          )}
          {inTransition ? <p className="team-transition">{messages.team.nextWord}</p> : (
            <Board rows={currentRows} cells={input.cells} pinned={input.pinned} onSelectCell={input.selectCell}
              shake={false} won={false} maxAttempts={MAX_ATTEMPTS} />
          )}
        </div>
      </main>
      <Keyboard states={keyboardStates} onPress={input.press} disabled={!started || inTransition || remainingMs <= 0} busy={sending} />
    </div>
  );
}

function SpectatorView({ state, onBack }: { state: TeamRaceState; onBack: () => void }) {
  const [lane, setLane] = useState<"X" | "Y">("X");
  const [remainingMs, setRemainingMs] = useState(
    Math.max(0, state.endsAt - Math.max(serverNow(), state.startsAt)),
  );
  useEffect(() => {
    const id = window.setInterval(() => setRemainingMs(Math.max(0, state.endsAt - Math.max(serverNow(), state.startsAt))), 200);
    return () => window.clearInterval(id);
  }, [state.endsAt]);
  const selected = state.players.filter((player) => player.lane === lane);
  const otherLane = lane === "X" ? "Y" : "X";
  const other = state.players.filter((player) => player.lane === otherLane);
  return (
    <div className="app team-app team-app--spectate">
      <ScreenHeader title={messages.spectate.title} subtitle={messages.team.title} onBack={onBack}
        actions={<span className="eyebrow eyebrow--live">{messages.spectate.live}</span>} />
      <TeamScore state={state} remainingMs={remainingMs} />
      <main className="overflow-y-auto px-4 pb-6">
        <button type="button" className="team-lane-preview" onClick={() => setLane(otherLane)}>
          <b>{messages.team.lane(otherLane)}</b>
          <span>{other.map((player, index) => <span key={player.user.id}>{index > 0 && " · "}<UserName user={player.user} /> {player.wordsSolved}</span>)}</span>
          <div>{other.map((player) => <SmallPlayerBoard key={player.user.id} player={player} />)}</div>
        </button>
        <h2 className="team-selected-lane">{messages.team.lane(lane)}</h2>
        <div className="team-spectate-boards">
          {selected.map((player) => <SmallPlayerBoard key={player.user.id} player={player} full />)}
        </div>
      </main>
    </div>
  );
}

function ResultView({ state, result, onBack, onLeave }: { state: TeamRaceState | null; result: TeamHistoryEntry; onBack: () => void; onLeave: () => void }) {
  const meId = useSession((session) => session.user?.id);
  const me = result.players.find((player) => player.user.id === meId);
  const won = Boolean(me && result.winnerTeam === me.team);
  const draw = result.winnerTeam === null;
  const spectator = state?.role === "spectator" || !me;
  const [notice, setNotice] = useState<string | null>(null);
  const voted = useRef(false);
  return (
    <div className="app team-app">
      {!spectator && <ResultEffects matchId={result.matchId} outcome={draw ? "draw" : won ? "win" : "loss"} />}
      <ScreenHeader title={messages.team.matchOver} onBack={onBack} />
      <main className="overflow-y-auto px-4 pb-6">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-5">
          {notice && <p className="toast-static">{notice}</p>}
          <section className="team-result-hero" data-outcome={draw ? "draw" : won ? "win" : "loss"}>
            <span className="eyebrow">{result.reason === "forfeit" ? messages.team.forfeit : messages.team.matchOver}</span>
            <h1>{spectator ? (draw ? messages.team.draw : `${teamName(result.winnerTeam!)} · ${messages.duel.won}`) : draw ? messages.team.draw : won ? messages.team.won : messages.team.lost}</h1>
            <div className="team-result-score"><b>A {result.score.A.words}</b><span>:</span><b>{result.score.B.words} B</b></div>
            <p>{result.reason === "fewer-attempts" ? messages.team.attemptsReason : result.reason === "more-words" ? messages.team.scoreReason : ""}</p>
          </section>
          <div className="team-result-players">
            {result.players.map((player) => (
              <article key={player.user.id} data-team={player.team}>
                <h3>{<UserName user={player.user} />} · {player.lane}</h3>
                <p>{player.wordsSolved} {messages.team.words} · {player.completedAttempts} {messages.team.attempts}</p>
                {player.rounds.length === 0 ? <small>{messages.team.noRounds}</small> : player.rounds.map((round) => (
                  <div key={round.wordIndex} className="team-round-result"><b>{round.answer}</b><ResultBoard rows={round.guesses} /></div>
                ))}
              </article>
            ))}
          </div>
          {/* Oy hatasız gitmezse (biri lobiden ayrıldıysa) sebebi görünür olsun. */}
          {!spectator && <button type="button" className="btn-primary" onClick={() => {
            if (voted.current) return;
            voted.current = true;
            void leaveResultThroughAd().then(() => getSocket().emit("team:rematch:vote", { matchId: result.matchId }, (response: AckResult<{ votes: number }>) => {
              if (!response.ok) {
                voted.current = false;
                setNotice(errorMessage(response));
              }
            }));
          }}>{messages.team.rematchVotes(result.rematchVotes)}</button>}
          {!spectator && <button type="button" className="btn-secondary" onClick={onBack}>{messages.team.returnLobby}</button>}
          {!spectator && <button type="button" className="btn-ghost" onClick={onLeave}>{messages.team.leave}</button>}
          {spectator && <button type="button" className="btn-secondary" onClick={onBack}>{messages.team.browse}</button>}
        </div>
      </main>
    </div>
  );
}

export function TeamScreen({ onBack, registerBackHandler, inviteCode = null, onInviteHandled }: TeamScreenProps) {
  const phase = useTeamStore((state) => state.phase);
  const lobbies = useTeamStore((state) => state.lobbies);
  const lobby = useTeamStore((state) => state.lobby);
  const match = useTeamStore((state) => state.match);
  const result = useTeamStore((state) => state.result);
  const [createOpen, setCreateOpen] = useState(false);
  const [codeOpen, setCodeOpen] = useState(false);
  const [settings, setSettings] = useState<TeamLobbySettings>({ ...STANDARD_TEAM_LOBBY_SETTINGS });
  const [code, setCode] = useState("");
  const [full, setFull] = useState<TeamLobbySummary | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [browserTab, setBrowserTab] = useState<"waiting" | "live">("waiting");
  const [helpOpen, setHelpOpen] = useState(false);
  const socket = getSocket();

  const openHelp = useCallback(() => {
    window.history.pushState({ mkScreen: "team", mkOverlay: "team-help" }, "");
    setHelpOpen(true);
  }, []);
  const closeHelp = useCallback(() => {
    if (helpOpen) window.history.back();
  }, [helpOpen]);

  useEffect(() => {
    if (phase === "idle") useTeamStore.getState().setBrowser();
    socket.emit("team:lobbies:sync", (response: AckResult<TeamLobbySummary[]>) => {
      if (response.ok) useTeamStore.getState().setLobbies(response.data);
    });
  }, [phase, socket]);

  const join = (summary: TeamLobbySummary) => socket.emit("team:lobby:join", { lobbyId: summary.lobbyId }, (response: AckResult<TeamLobbyState>) => {
    if (!response.ok) setNotice(errorMessage(response));
    else useTeamStore.getState().setLobby(response.data);
  });
  const watch = (summary: TeamLobbySummary) => socket.emit("team:spectate:join", { matchId: summary.activeMatchId! }, (response: AckResult<TeamRaceState>) => {
    if (!response.ok) setNotice(errorMessage(response));
    else useTeamStore.getState().setMatch(response.data);
  });

  const joinByCode = (value: string) => socket.emit("team:lobby:join-code", { code: value }, (response: AckResult<TeamLobbyState | { full: true; summary: TeamLobbySummary }>) => {
    if (!response.ok) setNotice(errorMessage(response)); else if ("full" in response.data) { setFull(response.data.summary); setCodeOpen(false); } else { useTeamStore.getState().setLobby(response.data); setCodeOpen(false); }
  });

  // Davet bağlantısıyla gelindiyse bir kez katıl; zaten bir lobide ya da maçtaysa dokunma.
  const inviteHandled = useRef(false);
  useEffect(() => {
    if (!inviteCode || inviteHandled.current) return;
    inviteHandled.current = true;
    onInviteHandled?.();
    if (phase === "browser" || phase === "idle") joinByCode(inviteCode);
  });

  const leaveCurrent = () => {
    const leave = () => {
      useTeamStore.getState().exitSilently(match?.matchId ?? result?.matchId ?? null);
      if (match?.role === "spectator") socket.emit("team:spectate:leave", { matchId: match.matchId });
      else if (match && !result) socket.emit("team:match:forfeit", { matchId: match.matchId }, () => {});
      else if (result) socket.emit("team:lobby:leave", { lobbyId: result.lobbyId });
      else if (lobby) socket.emit("team:lobby:leave", { lobbyId: lobby.lobbyId });
      // Yalnız lobiden çıkan oyuncu 2v2 listesinde kalır; başka lobiye geçebilsin.
      if (lobby && !match && !result) return;
      onBack();
    };
    if (result && match?.role !== "spectator") void leaveResultThroughAd().then(leave);
    else leave();
  };

  const returnToLobby = () => {
    if (!result || match?.role === "spectator") { leaveCurrent(); return; }
    const matchId = result.matchId;
    void leaveResultThroughAd().then(() => socket.emit("team:return-lobby", { matchId }, (response: AckResult<TeamLobbyState>) => {
      if (!response.ok) { setNotice(errorMessage(response)); return; }
      // Ignore a late acknowledgement after a rematch has already started.
      if (useTeamStore.getState().match?.matchId !== matchId) return;
      useTeamStore.setState({ lobby: response.data, match: null, result: null, phase: "lobby", spectating: false });
    }));
  };

  useEffect(() => {
    if (helpOpen) return registerBackHandler(() => {
      setHelpOpen(false);
      return "consume";
    });
    if (phase === "browser" || phase === "idle") return;
    return registerBackHandler(() => {
      if (result) { returnToLobby(); return "block"; }
      useTeamStore.getState().exitSilently(match?.matchId ?? null);
      if (match?.role === "spectator") socket.emit("team:spectate:leave", { matchId: match.matchId });
      else if (match) socket.emit("team:match:forfeit", { matchId: match.matchId }, () => {});
      else if (lobby) socket.emit("team:lobby:leave", { lobbyId: lobby.lobbyId });
      return "allow";
    });
  }, [helpOpen, lobby, match, phase, registerBackHandler, result, socket]);
  if (result) return <ResultView state={match} result={result} onBack={returnToLobby} onLeave={leaveCurrent} />;
  if (match && (phase === "countdown" || phase === "live")) {
    // Oyuncu görünümü kendi kartına, takım arkadaşına ve koridor rakibine
    // dayanıyor; biri eksikse maç ortasında beyaz ekran yerine izleyici
    // düzenine düşüyoruz.
    const seats = match.role === "spectator" ? null : liveSeats(match);
    return seats
      ? <LivePlayerView state={match} seats={seats} onBack={leaveCurrent} />
      : <SpectatorView state={match} onBack={leaveCurrent} />;
  }
  if (lobby && phase === "lobby") return <>
    <LobbyView lobby={lobby} onBack={leaveCurrent} onHelp={openHelp} />
    <TeamHowTo open={helpOpen} onClose={closeHelp} />
  </>;

  const waiting = lobbies.filter((item) => item.status === "waiting");
  const live = lobbies.filter((item) => item.status === "live");
  const shownLobbies = browserTab === "waiting" ? waiting : live;
  return (
    <div className="app team-app">
      <ScreenHeader title={messages.team.title} subtitle={messages.team.browserSubtitle} onBack={onBack}
        actions={<TeamHelpButton onClick={openHelp} />} />
      <main className="overflow-y-auto px-4 pb-24">
        <div className="mx-auto flex w-full max-w-2xl flex-col gap-4">
          {notice && <p className="toast-static">{notice}</p>}
          <div className="team-browser-tabs" role="tablist" aria-label={messages.team.title}>
            <button id="team-waiting-tab" type="button" role="tab" aria-selected={browserTab === "waiting"} aria-controls="team-waiting-panel"
              onClick={() => setBrowserTab("waiting")}>
              <span>{messages.team.waitingLobbies}</span><b>{waiting.length}</b>
            </button>
            <button id="team-live-tab" type="button" role="tab" aria-selected={browserTab === "live"} aria-controls="team-live-panel"
              onClick={() => setBrowserTab("live")}>
              <span>{messages.team.liveLobbies}</span><b>{live.length}</b>
            </button>
          </div>
          <section id={`team-${browserTab}-panel`} role="tabpanel" aria-labelledby={`team-${browserTab}-tab`} className="team-browser-panel">
            <div className="team-lobby-list">
              {shownLobbies.length ? shownLobbies.map((item) => (
                <LobbyCard key={item.lobbyId} summary={item}
                  onJoin={() => browserTab === "waiting" && join(item)}
                  onWatch={() => browserTab === "live" && watch(item)} />
              )) : <p className="team-browser-empty">{browserTab === "waiting" ? messages.team.emptyWaiting : messages.team.emptyLive}</p>}
            </div>
          </section>
        </div>
      </main>
      <div className="team-browser-actions"><button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>{messages.team.createLobby}</button><button type="button" className="btn-secondary" onClick={() => setCodeOpen(true)}>{messages.team.joinCode}</button></div>
      <Sheet open={createOpen} title={messages.team.settings} onClose={() => setCreateOpen(false)}>
        <SettingsFields value={settings} onChange={setSettings} />
        <button type="button" className="btn-primary mt-5 w-full" onClick={() => socket.emit("team:lobby:create", { settings }, (response: AckResult<TeamLobbyState>) => {
          if (!response.ok) setNotice(errorMessage(response)); else { useTeamStore.getState().setLobby(response.data); setCreateOpen(false); }
        })}>{messages.team.create}</button>
      </Sheet>
      <Sheet open={codeOpen} title={messages.team.joinCode} onClose={() => setCodeOpen(false)}>
        <label className="field-label">{messages.team.code}<input value={code} maxLength={6} placeholder={messages.team.codePlaceholder} onChange={(event) => setCode(event.target.value.toUpperCase())} /></label>
        <button type="button" className="btn-primary mt-4 w-full" disabled={code.length !== 6} onClick={() => joinByCode(code)}>{messages.team.join}</button>
      </Sheet>
      <Sheet open={full !== null} title={messages.team.full} accent="flame" onClose={() => setFull(null)}><p className="text-sm text-muted">{messages.team.fullHint}</p>{full?.status === "live" && full.settings.spectatorsAllowed && <button type="button" className="btn-primary mt-4 w-full" onClick={() => { watch(full); setFull(null); }}>{messages.team.watch}</button>}<button type="button" className="btn-secondary mt-2 w-full" onClick={() => setFull(null)}>{messages.common.close}</button></Sheet>
      <TeamHowTo open={helpOpen} onClose={closeHelp} />
    </div>
  );
}
